import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { userPool } from 'app/login/adapters/userPoolCognito';

interface UserData {
  cognitoUser: CognitoUser;
  role: string | null;
}

interface AuthContextType {
  user: UserData | null;
  loading: boolean;
  setUser: (user: UserData | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let setUserRef: ((user: UserData | null) => void) | null = null;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  // Assign the setUser function to setUserRef
  setUserRef = setUser;

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();

    if (currentUser) {
      currentUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session?.isValid()) {
            setUser(null);
            setLoading(false);
          } else {
            currentUser.getUserAttributes((attrErr, attributes) => {
              if (attrErr || !attributes) {
                setUser(null);
              } else {
                const roleAttr = attributes.find(
                  (attr) => attr.getName() === "custom:role"
                );
                const role = roleAttr?.getValue() ?? null;

                setUser({
                  cognitoUser: currentUser,
                  role,
                });
              }
              setLoading(false);
            });
          }
        }
      );
    } else {
      setLoading(false);
    }
  }, []);

  const logout = () => {
    user?.cognitoUser.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const setUserExternal = (userData: UserData | null) => {
  if (setUserRef) {
    setUserRef(userData);
  } else {
    console.warn("setUser is not available yet.");
  }
};
